import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { tokenManager, securityUtils } from '../services/api';

// Custom hook for secure API calls with loading states
export const useSecureApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // Check authentication before API calls
  const checkAuth = useCallback(() => {
    const token = tokenManager.getToken();
    if (!token) {
      dispatch(logout());
      throw new Error('No authentication token found');
    }

    if (securityUtils.isTokenExpired(token)) {
      dispatch(logout());
      throw new Error('Authentication token has expired');
    }

    return true;
  }, [dispatch]);

  // Secure API call wrapper
  const secureCall = useCallback(async (apiFunction, ...args) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate authentication first
      checkAuth();
      
      // Execute the API call
      const result = await apiFunction(...args);
      
      return result;
    } catch (err) {
      const errorMessage = err.message || 'API call failed';
      setError(errorMessage);
      
      // Log error for debugging
      console.error('🔒 Secure API Call Error:', {
        function: apiFunction.name,
        args,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });
      
      // Handle authentication errors
      if (errorMessage.includes('token') || errorMessage.includes('auth')) {
        dispatch(logout());
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAuth, dispatch]);

  // Batch API calls with authentication
  const secureBatchCall = useCallback(async (apiCalls) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate authentication first
      checkAuth();
      
      // Execute all calls in parallel
      const results = await Promise.allSettled(apiCalls.map(call => call()));
      
      // Check for any failures
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
        console.warn(`${failures.length} out of ${apiCalls.length} API calls failed`);
      }
      
      return results;
    } catch (err) {
      const errorMessage = err.message || 'Batch API calls failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  return {
    loading,
    error,
    secureCall,
    secureBatchCall,
    clearError: () => setError(null)
  };
};

// Custom hook for token monitoring
export const useTokenMonitor = () => {
  const [tokenStatus, setTokenStatus] = useState('checking');
  const dispatch = useDispatch();

  const checkTokenStatus = useCallback(() => {
    const token = tokenManager.getToken();
    
    if (!token) {
      setTokenStatus('missing');
      return false;
    }
    
    if (securityUtils.isTokenExpired(token)) {
      setTokenStatus('expired');
      dispatch(logout());
      return false;
    }
    
    const expiration = securityUtils.getTokenExpiration(token);
    const timeUntilExpiry = expiration ? expiration.getTime() - Date.now() : 0;
    
    if (timeUntilExpiry < 5 * 60 * 1000) { // Less than 5 minutes
      setTokenStatus('expiring_soon');
    } else {
      setTokenStatus('valid');
    }
    
    return true;
  }, [dispatch]);

  return {
    tokenStatus,
    checkTokenStatus,
    isTokenValid: tokenStatus === 'valid' || tokenStatus === 'expiring_soon'
  };
};
